import React, { useCallback, useMemo, useState } from 'react';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { SHOW_LESS, SHOW_MORE } from 'common/constants/pageFilters';
import { Currency } from 'common/types/format';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';
import { useLayoutContext } from 'context/LayoutContext';
import { CurrencyFormatterProps, CurrencyMessageParams } from './GridFormatter.props';
import {
  StyledCurrencyButton,
  StyledCurrencyIcon,
  StyledMenuSeparator,
  StyledShowMoreLess,
} from './GridFormatter.styles';

const CurrencyFormatter = ({ currency, onCurrencyChanged, isLoading }: CurrencyFormatterProps) => {
  const foreignCurrency = currency.sourceCurrency !== currency.code;
  const { isExportMode } = useLayoutContext();
  const {
    usedCurrencies, currencies, showMore, showLess
  } = useCurrencies();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCurrencyChange = (selectedCurrency: Currency) => () => {
    if (!isLoading) {
      onCurrencyChanged(selectedCurrency);
      handleClose();
    }
  };

  const roundedExchangeRate = useMemo(
    () => (currency.exchangeRate ? Math.round(currency.exchangeRate * 100) / 100 : undefined),
    [currency.exchangeRate]
  );

  const getCurrencyMessage = useCallback(
    ({ sourceCurrency, exchangeRate, currencyName }: CurrencyMessageParams) => {
      const message = `1 ${sourceCurrency} -> ${exchangeRate} ${currency.code}`;
      if (currencyName) {
        return `${currencyName}: ${message}`;
      }
      return message;
    },
    [currency]
  );

  return (
    <>
      <Tooltip
        title={
          <div>
            <div>{getCurrencyMessage(currency)}</div>
            {currency.additionalCurrencies?.map(additionalCurrency => (
              <div key={additionalCurrency.sourceCurrency as string}>{getCurrencyMessage(additionalCurrency)}</div>
            ))}
          </div>
        }
        arrow>
        <span>
          <StyledCurrencyButton
            $foreignCurrency={foreignCurrency}
            $isExportMode={isExportMode}
            onClick={handleClick}
            disabled={isLoading}>
            <Grid container alignItems="center">
              <Grid>
                {foreignCurrency ? '1 ' : ''}
                {currency.sourceCurrency}
              </Grid>
              {foreignCurrency && (
                <>
                  <Grid>
                    <StyledCurrencyIcon />
                  </Grid>
                  <Grid>
                    {roundedExchangeRate} {currency.code}
                  </Grid>
                </>
              )}
            </Grid>
          </StyledCurrencyButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {currencies.map(c => {
          const isLastUsedCurrency = c.code === usedCurrencies[usedCurrencies.length - 1];
          const MenuItemComponent = isLastUsedCurrency ? StyledMenuSeparator : MenuItem;

          return (
            <MenuItemComponent key={c.code} onClick={handleCurrencyChange(c)}>
              {`${c.code} (${c.symbol})`}
            </MenuItemComponent>
          );
        })}
        <StyledShowMoreLess onClick={currencies.length === usedCurrencies.length ? showMore : showLess}>
          {currencies.length === usedCurrencies.length ? SHOW_MORE : SHOW_LESS}
        </StyledShowMoreLess>
      </Menu>
    </>
  );
};

export default CurrencyFormatter;
